import type { Session } from "@/api/auth";

/**
 * "Sign in with Vonix": authorization code with PKCE against the portal's
 * own OAuth2 server, then the portal's mint endpoint, which hands back a
 * Crossbar token for the company's Offhook account.
 *
 * Three round trips, all against the portal origin:
 *
 * 1. `GET  /oauth/authorize` — the portal's own page. Our client is
 *    registered with `external: false`, so it approves without a consent
 *    screen and redirects back to `/auth/portal/callback?code=&state=`.
 * 2. `POST /oauth/token` — form-encoded, `authorization_code` with the
 *    verifier. The portal is a public client here: it compares the stored
 *    client secret against the one sent, so the `oauth_clients` row must
 *    carry no secret and we send none.
 * 3. `POST /api/v1/pbx/token` — the portal mints through the tie line and
 *    answers with the Crossbar token plus the API and socket origins to
 *    spend it at. The shared secret between the portal and the tie line
 *    never comes near the browser.
 *
 * The portal access token is held in memory only, long enough to re-mint;
 * a page reload loses it, and the next renewal walks the redirect again
 * (invisible, the client is auto-approved).
 */

/** Where the portal sends the browser back. Must match the `oauth_clients` row exactly. */
export const CALLBACK_PATH = "/auth/portal/callback";

/** Survives the redirect; cleared the moment the callback reads it. */
const PENDING_KEY = "switchboard-portal-signin";

/** RFC 7636 wants 43-128 characters; 64 bytes base64url is 86. */
const VERIFIER_BYTES = 64;
const STATE_BYTES = 16;

/** Re-minting needs a little room before the portal's own token lapses. */
const GRANT_SKEW_S = 60;

export interface PortalConfig {
  origin: string;
  clientId: string;
}

/** A sign-in that cannot go on. `reason` is the portal's own code where it gave one. */
export class PortalSignInError extends Error {
  readonly reason?: string;
  constructor(message: string, reason?: string) {
    super(message);
    this.name = "PortalSignInError";
    this.reason = reason;
  }
}

/** The held grant is gone (or never existed): only a fresh redirect can renew. */
export class PortalReauthRequired extends Error {
  constructor() {
    super("The portal session has to be established again.");
    this.name = "PortalReauthRequired";
  }
}

/** One key of `window.__SWITCHBOARD_CONFIG__`, trimmed, or "" where it is not set. */
function runtimeValue(key: "portalOrigin" | "portalClientId"): string {
  const value = typeof window === "undefined" ? undefined : window.__SWITCHBOARD_CONFIG__?.[key];
  return typeof value === "string" ? value.trim() : "";
}

/**
 * The deployment values, runtime first.
 *
 * One image serves every environment (the lab, dev, production), and
 * their portals differ, so the pair cannot be baked into the bundle:
 * whatever serves the built app serves `/config.js` beside `index.html`,
 * which index.html loads before the bundle and which defines
 * `window.__SWITCHBOARD_CONFIG__` (nginx renders it from PORTAL_ORIGIN
 * and PORTAL_CLIENT_ID — see docker/default.conf.template). The `VITE_`
 * variables are the fallback, so a `.env` still works under `pnpm dev`.
 *
 * Both values are needed: an origin on its own cannot name a client to
 * the portal, so the button stays hidden until the pair is set — which
 * is what a missing /config.js, or one with empty values, leaves.
 */
export function portalConfig(): PortalConfig | undefined {
  const origin = (runtimeValue("portalOrigin") || (import.meta.env.VITE_PORTAL_ORIGIN ?? "").trim()).replace(/\/+$/, "");
  const clientId = runtimeValue("portalClientId") || (import.meta.env.VITE_PORTAL_CLIENT_ID ?? "").trim();
  if (!origin || !clientId) return undefined;
  return { origin, clientId };
}

export const portalSignInAvailable = () => portalConfig() !== undefined;

function required(): PortalConfig {
  const cfg = portalConfig();
  if (!cfg) throw new PortalSignInError("This console was not built with a Vonix portal to sign in against.");
  return cfg;
}

/* ---- PKCE ---- */

export function base64Url(bytes: Uint8Array): string {
  let binary = "";
  for (const b of bytes) binary += String.fromCharCode(b);
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

const randomBase64Url = (length: number) => base64Url(crypto.getRandomValues(new Uint8Array(length)));

/** The code verifier: unreserved characters only, so it survives a form body untouched. */
export const randomVerifier = () => randomBase64Url(VERIFIER_BYTES);
export const randomState = () => randomBase64Url(STATE_BYTES);

/** S256: base64url of the SHA-256 of the verifier's ASCII bytes. */
export async function codeChallenge(verifier: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return base64Url(new Uint8Array(digest));
}

/* ---- the leg across the redirect ---- */

export interface PendingSignIn {
  state: string;
  verifier: string;
  /** Where the operator was headed before they were sent to sign in. */
  from: string;
}

export function rememberSignIn(pending: PendingSignIn): void {
  sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
}

export function forgetSignIn(): void {
  sessionStorage.removeItem(PENDING_KEY);
}

/**
 * Read back the leg this callback belongs to and clear it. A state that
 * does not match the one we stored is the one thing PKCE cannot cover,
 * so it is refused outright rather than exchanged.
 */
export function takeSignIn(state: string | null): PendingSignIn {
  const raw = sessionStorage.getItem(PENDING_KEY);
  forgetSignIn();
  if (!raw) throw new PortalSignInError("This sign-in did not start in this browser tab. Try again.", "no_pending_sign_in");
  let pending: PendingSignIn;
  try {
    pending = JSON.parse(raw) as PendingSignIn;
  } catch {
    throw new PortalSignInError("The stored sign-in could not be read. Try again.", "no_pending_sign_in");
  }
  if (!state || !pending.state || state !== pending.state) {
    throw new PortalSignInError("The sign-in came back with the wrong state and was refused.", "state_mismatch");
  }
  return pending;
}

export const callbackUrl = () => `${window.location.origin}${CALLBACK_PATH}`;

/**
 * The parameters the portal's authorization server reads
 * (`portal-webapp/internal/oauth/handler.go`, `auth-service` Authorize).
 * `response_type` is the only one it insists on beyond the client id and
 * a redirect URI that is on the client's allowlist.
 */
export function authorizeUrl(cfg: PortalConfig, p: { state: string; challenge: string; redirectUri: string }): string {
  const url = new URL(`${cfg.origin}/oauth/authorize`);
  url.searchParams.set("client_id", cfg.clientId);
  url.searchParams.set("redirect_uri", p.redirectUri);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("scope", "openid");
  url.searchParams.set("state", p.state);
  url.searchParams.set("code_challenge", p.challenge);
  url.searchParams.set("code_challenge_method", "S256");
  return url.href;
}

/** Leave for the portal. Never returns: the tab navigates away. */
export async function startPortalSignIn(from = "/"): Promise<void> {
  const cfg = required();
  const verifier = randomVerifier();
  const state = randomState();
  rememberSignIn({ state, verifier, from });
  window.location.assign(authorizeUrl(cfg, { state, challenge: await codeChallenge(verifier), redirectUri: callbackUrl() }));
}

/* ---- the token endpoint ---- */

interface Grant {
  accessToken: string;
  refreshToken?: string;
  /** Unix seconds, from the portal's `expires_in`. */
  expiresAt?: number;
}

/** In memory only, and only so a lapsing Crossbar token can be re-minted. */
let grant: Grant | undefined;

export const heldGrant = () => grant;
export const forgetGrant = () => void (grant = undefined);

async function readJson(res: Response): Promise<Record<string, unknown> | undefined> {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text) as Record<string, unknown>;
  } catch {
    return undefined;
  }
}

const str = (o: Record<string, unknown> | undefined, key: string): string | undefined => (typeof o?.[key] === "string" ? (o[key] as string) : undefined);
const num = (o: Record<string, unknown> | undefined, key: string): number | undefined => (typeof o?.[key] === "number" ? (o[key] as number) : undefined);

async function postTokenEndpoint(cfg: PortalConfig, body: Record<string, string>): Promise<Grant> {
  let res: Response;
  try {
    res = await fetch(`${cfg.origin}/oauth/token`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded", Accept: "application/json" },
      body: new URLSearchParams(body).toString(),
    });
  } catch {
    throw new PortalSignInError("The Vonix portal could not be reached.", "unreachable");
  }
  const parsed = await readJson(res);
  if (!res.ok) {
    throw new PortalSignInError(str(parsed, "error_description") ?? "The Vonix portal refused the sign-in.", str(parsed, "error"));
  }
  const accessToken = str(parsed, "access_token");
  if (!accessToken) throw new PortalSignInError("The Vonix portal answered without an access token.", "no_access_token");
  const expiresIn = num(parsed, "expires_in");
  return {
    accessToken,
    refreshToken: str(parsed, "refresh_token"),
    expiresAt: expiresIn ? Math.floor(Date.now() / 1000) + expiresIn : undefined,
  };
}

/* ---- the mint endpoint ---- */

/** `POST /api/v1/pbx/token`, the portal's answer (`internal/pbx/handler.go`). */
export interface MintedToken {
  token: string;
  expiresAt?: number;
  accountId: string;
  ownerId: string;
  apiOrigin?: string;
  socketOrigin?: string;
}

/**
 * The refusals the mint endpoint and the tie line behind it answer with.
 * Anything unlisted falls back to the reason code itself.
 */
const MINT_REASONS: Record<string, string> = {
  invalid_token: "The portal sign-in was not accepted. Try signing in again.",
  not_admin: "Your portal user is not an administrator of this company.",
  not_offhook: "This company is not on the Offhook platform yet.",
  user_not_synced: "Your portal user has not reached the phone system yet. Try again shortly.",
  account_not_managed: "This company's phone system is not managed here.",
  instance_unavailable: "The phone system for this company is not reachable right now.",
  tieline_unreachable: "The phone system for this company is not reachable right now.",
};

export async function mintKazooToken(cfg: PortalConfig, accessToken: string): Promise<MintedToken> {
  let res: Response;
  try {
    res = await fetch(`${cfg.origin}/api/v1/pbx/token`, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, Accept: "application/json" },
    });
  } catch {
    throw new PortalSignInError("The Vonix portal could not be reached.", "unreachable");
  }
  const parsed = await readJson(res);
  if (!res.ok) {
    const reason = str(parsed, "error");
    throw new PortalSignInError(MINT_REASONS[reason ?? ""] ?? `The portal refused to issue a phone system token (${reason ?? res.status}).`, reason);
  }
  const token = str(parsed, "token");
  const accountId = str(parsed, "accountId");
  const ownerId = str(parsed, "ownerId");
  if (!token || !accountId) throw new PortalSignInError("The portal answered without a phone system token.", "no_token");
  return {
    token,
    accountId,
    ownerId: ownerId ?? "",
    expiresAt: num(parsed, "expiresAt"),
    apiOrigin: str(parsed, "apiOrigin") || undefined,
    socketOrigin: str(parsed, "socketOrigin") || undefined,
  };
}

/** The console's session, as a minted token describes it. */
export function sessionFrom(minted: MintedToken): Session {
  return {
    token: minted.token,
    accountId: minted.accountId,
    ownerId: minted.ownerId,
    isReseller: false,
    expiresAt: minted.expiresAt,
    origin: "portal",
    apiOrigin: minted.apiOrigin,
    socketOrigin: minted.socketOrigin,
  };
}

/* ---- the two entry points the console calls ---- */

/**
 * The callback route: exchange the code, mint, and say where the operator
 * was headed. `search` is `window.location.search`.
 */
export async function completePortalSignIn(search: string): Promise<{ session: Session; from: string }> {
  const cfg = required();
  const params = new URLSearchParams(search);
  const denial = params.get("error");
  if (denial) {
    forgetSignIn();
    throw new PortalSignInError(params.get("error_description") ?? "The Vonix portal refused the sign-in.", denial);
  }
  const pending = takeSignIn(params.get("state"));
  const code = params.get("code");
  if (!code) throw new PortalSignInError("The portal came back without an authorization code.", "no_code");

  grant = await postTokenEndpoint(cfg, {
    grant_type: "authorization_code",
    client_id: cfg.clientId,
    code,
    code_verifier: pending.verifier,
    redirect_uri: callbackUrl(),
  });
  return { session: sessionFrom(await mintKazooToken(cfg, grant.accessToken)), from: pending.from };
}

/**
 * Renew a portal session: refresh the portal grant when it gave us a
 * refresh token, otherwise re-mint on the access token we still hold.
 * With neither, only a fresh redirect will do.
 */
export async function renewPortalSession(now: number = Date.now()): Promise<Session> {
  const cfg = required();
  const held = grant;
  if (held?.refreshToken) {
    grant = await postTokenEndpoint(cfg, { grant_type: "refresh_token", client_id: cfg.clientId, refresh_token: held.refreshToken });
    return sessionFrom(await mintKazooToken(cfg, grant.accessToken));
  }
  if (held?.accessToken && (held.expiresAt === undefined || held.expiresAt > Math.floor(now / 1000) + GRANT_SKEW_S)) {
    return sessionFrom(await mintKazooToken(cfg, held.accessToken));
  }
  throw new PortalReauthRequired();
}
